import { Factuur } from '../services/orderhistory/factuurService';
import { OrderHistory, TaxCalculationMethod } from '../stores/appOrderHistoryStore';
import { PlatformSectionContext } from '../stores/appRestaurantStore';
import { getOrderVatAmount, getOrderVatRate } from '../components/orderhistory/vatUtils';
import { getTableFormattedDate, toDate } from './dateUtils';
import { calculateProductTotal } from './platformUtils';

export interface FactuurTableRow {
  id: string;
  orderNumber: string;
  orderNumbers: string;
  orderDate: string;
  invoiceDate?: string;
  restaurantName: string;
  platform: OrderHistory['platform'];
  unknownSupplierName?: string;
  orderCount: number;
  totalExVat: number;
  totalInclVat: number;
  vatAmount: number;
  vatRates: (number | undefined)[];
  taxCalculationMethod?: TaxCalculationMethod;
  fileUrl?: string;
  productNames: string;
  splitByCategory: boolean;
  totalMismatch: boolean;
}

/** Own invoice lines, already enriched and price-adjusted by the API. Never read the order store. */
export function factuurOrdersToOrderHistory(
  factuur: Factuur,
  platformToSectionIndex: Record<string, PlatformSectionContext>
): OrderHistory[] {
  return factuur.orders.map(order => ({
    id: order.factuurOrderId,
    restaurantPlatformId: order.restaurantPlatformId,
    restaurantId: factuur.restaurantId,
    restaurantSection: platformToSectionIndex[order.restaurantPlatformId],
    messageId: '', gmailId: '', clientName: '',
    platform: factuur.platform,
    orderNumber: order.orderNumber,
    orderDate: toDate(order.orderDate),
    total: order.total ?? order.productData.reduce((sum, product) => sum + calculateProductTotal(product, factuur.platform), 0),
    totalWithTaxes: order.totalWithTaxes ?? undefined,
    productData: order.productData,
    taxRate: order.taxRate,
    taxCalculationMethod: order.taxCalculationMethod,
    sourceFileUrl: order.orderSourceFileUrl ?? undefined,
  }));
}

/** Keep an invoice without order rows filterable by its restaurant/section/supplier too. */
export function factuurOrdersForFiltering(
  factuur: Factuur,
  platformToSectionIndex: Record<string, PlatformSectionContext>
): OrderHistory[] {
  const orders = factuurOrdersToOrderHistory(factuur, platformToSectionIndex);
  if (orders.length) return orders;
  return [{
    id: factuur.id,
    restaurantPlatformId: factuur.restaurantPlatformId,
    restaurantId: factuur.restaurantId,
    restaurantSection: platformToSectionIndex[factuur.restaurantPlatformId],
    messageId: '', gmailId: '', clientName: '',
    platform: factuur.platform,
    orderNumber: '', orderDate: toDate(factuur.invoiceDate),
    total: factuur.total, totalWithTaxes: factuur.totalWithTaxes ?? undefined,
    productData: [],
  }];
}

/** Regroup after the existing order filter chain. Header amounts remain printed unless lines filter. */
export function regroupFacturen(
  facturen: Factuur[],
  filteredOrders: OrderHistory[],
  useLineTotals: boolean,
  restaurantNames: ReadonlyMap<string, string>
): FactuurTableRow[] {
  const ordersById = new Map(filteredOrders.map(order => [order.id, order]));
  return facturen.flatMap(factuur => {
    const orders = factuur.orders.length
      ? factuur.orders.flatMap(order => {
          const match = ordersById.get(order.factuurOrderId);
          return match ? [match] : [];
        })
      : (ordersById.has(factuur.id) ? [ordersById.get(factuur.id)!] : []);
    if (!orders.length) return [];
    const totalExVat = useLineTotals ? orders.reduce((sum, order) => sum + order.total, 0) : factuur.total;
    const totalInclVat = useLineTotals
      ? orders.reduce((sum, order) => sum + (order.totalWithTaxes ?? order.total), 0)
      : factuur.totalWithTaxes ?? factuur.total;
    const shipping = useLineTotals ? 0 : factuur.shipping ?? 0;
    const products = orders.flatMap(order => order.productData);
    const estimatedOrder = orders.find(order => order.taxCalculationMethod !== 'provided');
    const lineRates = products.map(product => product.taxRate).filter(rate => rate !== undefined);
    return [{
      id: factuur.id,
      orderNumber: factuur.invoiceNumber,
      orderNumbers: factuur.orders.map(order => order.orderNumber).join(' ').toLowerCase(),
      orderDate: getTableFormattedDate(toDate(factuur.invoiceDate)),
      invoiceDate: factuur.invoiceDate,
      restaurantName: restaurantNames.get(factuur.restaurantId) || '--',
      platform: factuur.platform,
      unknownSupplierName: undefined,
      orderCount: factuur.orders.length,
      totalExVat,
      totalInclVat,
      vatAmount: !useLineTotals && factuur.totalWithTaxes == null ? 0
        : getOrderVatAmount(factuur.platform, totalInclVat, totalExVat, shipping),
      vatRates: lineRates.length ? lineRates : [factuur.totalWithTaxes == null ? undefined
        : getOrderVatRate(factuur.platform, totalInclVat, totalExVat, shipping, products)],
      taxCalculationMethod: estimatedOrder ? estimatedOrder.taxCalculationMethod : 'provided' as const,
      fileUrl: factuur.sourceFileUrl ?? undefined,
      productNames: products.map(product => product.name).join(' ').toLowerCase(),
      splitByCategory: false,
      totalMismatch: false,
    }];
  }).sort((a, b) => toDate(b.invoiceDate).getTime() - toDate(a.invoiceDate).getTime());
}
